import * as muiColors from '@mui/material/colors';

type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const getColorFromPalette = (hash: number, shade: number): string => {
  const colorKeys = Object.keys(muiColors).filter((colorKey) => !['common', 'grey'].includes(colorKey));
  const colorKey = colorKeys[hash % colorKeys.length];
  const color = (muiColors as any)[colorKey][shade];
  return color || (muiColors as any)[colorKey][500];
};

export const getColorFromString = (value: string, shade: Shade): string => {
  if (!value) return '';
  const hash = hashCode(value);
  return getColorFromPalette(hash, shade);
};

const rgbaRegex = /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/;
const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;
const hexRegex = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;

/**
 * Set the opacity of a color string (either RGBA or HEX format).
 *
 * @param {string} color - The original color string.
 * @param {number} opacity - The new opacity value between 0 and 1.
 * @returns {string} A new RGBA color string with the updated opacity.
 */
export const setOpacity = (color: string, opacity: number): string => {
  const rgbaMatch = color.match(rgbaRegex);
  const rgbMatch = color.match(rgbRegex);
  const hexMatch = color.match(hexRegex);

  if (rgbaMatch) {
    const [_, r, g, b] = rgbaMatch;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } else if (rgbMatch) {
    const [_, r, g, b] = rgbMatch;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } else if (hexMatch) {
    let hexColor = hexMatch[1];
    // Convert short form (#RGB) to long form (#RRGGBB)
    if (hexColor.length === 3) {
      hexColor = hexColor
        .split('')
        .map((c) => c + c)
        .join('');
    }
    const [r, g, b] = [
      parseInt(hexColor.substring(0, 2), 16),
      parseInt(hexColor.substring(2, 4), 16),
      parseInt(hexColor.substring(4, 6), 16)
    ];
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return color; // If it doesn't match any regex, return the original color
};

/**
 * Adjust the brightness of a color string (either RGBA, HEX, or short HEX format).
 *
 * @param {string} color - The original color string.
 * @param {number} brightness - The brightness factor where 1 is unchanged, greater than 1 is brighter, and between 0 and 1 is darker.
 * @returns {string} A new color string with the updated brightness.
 */
export const adjustBrightness = (color: string, brightness: number): string => {
  const rgbaMatch = color.match(rgbaRegex);
  const rgbMatch = color.match(rgbRegex);
  const hexMatch = color.match(hexRegex);

  if (rgbaMatch) {
    const [_, r, g, b, a] = rgbaMatch;
    const newR = Math.min(255, Math.max(0, Math.round(Number(r) * brightness)));
    const newG = Math.min(255, Math.max(0, Math.round(Number(g) * brightness)));
    const newB = Math.min(255, Math.max(0, Math.round(Number(b) * brightness)));
    return `rgba(${newR}, ${newG}, ${newB}, ${a})`;
  } else if (rgbMatch) {
    const [_, r, g, b] = rgbMatch;
    const newR = Math.min(255, Math.max(0, Math.round(Number(r) * brightness)));
    const newG = Math.min(255, Math.max(0, Math.round(Number(g) * brightness)));
    const newB = Math.min(255, Math.max(0, Math.round(Number(b) * brightness)));
    return `rgb(${newR}, ${newG}, ${newB})`;
  } else if (hexMatch) {
    let hexColor = hexMatch[1];
    // Convert short form (#RGB) to long form (#RRGGBB)
    if (hexColor.length === 3) {
      hexColor = hexColor
        .split('')
        .map((c) => c + c)
        .join('');
    }
    let [r, g, b] = [
      parseInt(hexColor.substring(0, 2), 16),
      parseInt(hexColor.substring(2, 4), 16),
      parseInt(hexColor.substring(4, 6), 16)
    ];
    r = Math.min(255, Math.max(0, Math.round(r * brightness)));
    g = Math.min(255, Math.max(0, Math.round(g * brightness)));
    b = Math.min(255, Math.max(0, Math.round(b * brightness)));
    const newHex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    return newHex;
  }

  return color; // If it doesn't match any regex, return the original color
};
